import React, { useEffect, useRef, useState } from "react";
import { CloudUpload, MoreVertical, Phone, Navigation, MessageSquare, Star } from "lucide-react";
import { SOURCE_ICONS } from "./constants";

/* 3-dot action menu used in rows */
export function ActionMenu({ prospect, navigate }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("touchstart", close);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("touchstart", close); };
  }, []);
  return (
    <div ref={ref} className="relative shrink-0" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-8 h-8 rounded-lg grid place-items-center hover:bg-[var(--surface-alt)] active:scale-95 transition-colors text-zinc-400"
        aria-label="Actions" data-testid="pipeline-action-menu-btn"
      >
        <MoreVertical className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-9 z-50 bg-[var(--surface)] border border-[var(--border)] shadow-floating rounded-xl overflow-hidden min-w-[160px]">
          {[
            { label: "Appeler", Icon: Phone, path: `/phone/${prospect.prospect_id}` },
            { label: "Visiter", Icon: Navigation, path: `/visit/${prospect.prospect_id}` },
            { label: "Message", Icon: MessageSquare, path: `/social/${prospect.prospect_id}` },
          ].map((a) => (
            <button key={a.label} onClick={() => { setOpen(false); navigate(a.path); }}
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[var(--surface-alt)] transition-colors">
              <a.Icon className="w-4 h-4 text-zinc-400 shrink-0" strokeWidth={1.8} />
              <span className="text-sm font-medium text-[var(--text)]">{a.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function ScoreBadge({ score }) {
  const cls =
    score >= 80 ? "text-emerald-300 border-emerald-500/30 bg-emerald-500/10" :
    score >= 60 ? "text-amber-300 border-amber-500/30 bg-amber-500/10" :
                  "text-zinc-400 border-zinc-700 bg-zinc-800/40";
  return (
    <span className={`text-[11px] font-semibold tabular-nums px-1.5 py-0.5 rounded-md border ${cls}`}>
      {score}%
    </span>
  );
}

export function ProspectRow({ p, onClick, pending, navigate }) {
  const SourceIcon = SOURCE_ICONS[p.source] || Star;
  const lastDate = p.last_contact ? new Date(p.last_contact).toLocaleDateString("fr-FR") : "";
  const initial = (p.name || "?").charAt(0).toUpperCase();
  return (
    <div className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-[var(--surface-alt)]/60 transition-colors"
      data-testid={`pipeline-card-${p.prospect_id}`}>
      <button onClick={onClick} className="w-10 h-10 rounded-lg bg-[var(--surface-alt)] border border-[var(--border)] grid place-items-center text-zinc-200 font-semibold text-sm shrink-0">
        {initial}
      </button>
      <button onClick={onClick} className="flex-1 min-w-0 text-left">
        <div className="flex items-center gap-2">
          <div className="font-medium text-[14px] text-[var(--text)] truncate">{p.name || "Sans nom"}</div>
          {pending && (
            <span data-testid="pending-sync-indicator" className="bg-amber-500/15 text-amber-300 border border-amber-500/30 rounded-md px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider flex items-center gap-0.5">
              <CloudUpload className="w-2.5 h-2.5" /> sync
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 text-xs text-zinc-500 mt-0.5">
          <SourceIcon className="w-3 h-3 shrink-0" />
          <span className="truncate">{p.company || "—"}</span>
          {lastDate && <span className="text-zinc-600">· {lastDate}</span>}
        </div>
      </button>
      <div className="flex items-center gap-2 shrink-0">
        <ScoreBadge score={p.score || 0} />
        <ActionMenu prospect={p} navigate={navigate} />
      </div>
    </div>
  );
}

export function TempProspectRow({ p, onClick, navigate }) {
  return (
    <div className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-[var(--surface-alt)]/60 transition-colors"
      data-testid={`list-item-${p.prospect_id}`}>
      <button onClick={onClick} className="w-10 h-10 rounded-lg bg-[var(--surface-alt)] border border-[var(--border)] grid place-items-center text-zinc-200 font-display font-semibold shrink-0">
        {(p.name || "?").charAt(0).toUpperCase()}
      </button>
      <button onClick={onClick} className="flex-1 min-w-0 text-left">
        <div className="font-medium text-[14px] text-[var(--text)] truncate">{p.name || "Sans nom"}</div>
        <div className="text-xs text-zinc-500 truncate">{p.company || "—"}</div>
      </button>
      <div className="flex items-center gap-2 shrink-0">
        <ScoreBadge score={p.score || 0} />
        <ActionMenu prospect={p} navigate={navigate} />
      </div>
    </div>
  );
}
